import { useState, useEffect, useRef } from 'react';
import { autocompleteAdresse } from '../../lib/api';

export default function AddressAutocomplete({ value, onChange, onSelect, error }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Fermer si clic extérieur
  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleChange(e) {
    const val = e.target.value;
    onChange(val);

    // Debounce 400ms
    clearTimeout(debounceRef.current);
    if (val.length < 3) { setSuggestions([]); setOpen(false); return; }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const results = await autocompleteAdresse(val);
      setSuggestions(results);
      setOpen(results.length > 0);
      setLoading(false);
    }, 400);
  }

  function handleSelect(suggestion) {
    onChange(suggestion.label);
    onSelect?.(suggestion);
    setOpen(false);
    setSuggestions([]);
  }

  return (
    <div ref={containerRef} style={{ display: 'flex', flexDirection: 'column', gap: '6px', position: 'relative' }}>
      <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-2)' }}>Adresse *</label>
      <div style={{ position: 'relative' }}>
        <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 15, pointerEvents: 'none' }}>📍</span>
        <input
          value={value}
          onChange={handleChange}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder="12 rue de la Paix, Paris…"
          autoComplete="off"
          style={{
            width: '100%',
            padding: '10px 36px 10px 38px',
            border: `1.5px solid ${error ? '#EF4444' : open ? 'var(--brand)' : 'var(--border)'}`,
            borderRadius: '10px', fontSize: '14px', outline: 'none',
            background: 'var(--surface)', color: 'var(--text)',
            transition: 'border-color 0.2s',
          }}
        />
        {/* Indicateur chargement */}
        {loading && (
          <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, border: '2px solid var(--brand)30', borderTopColor: 'var(--brand)', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
        )}
      </div>

      {/* Liste de suggestions */}
      {open && suggestions.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 500,
          background: 'var(--surface)', border: '1.5px solid var(--brand)',
          borderRadius: '12px', boxShadow: 'var(--shadow-lg)',
          overflow: 'hidden', marginTop: 4,
          animation: 'scaleIn 0.15s cubic-bezier(0.4,0,0.2,1)',
        }}>
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              onMouseDown={() => handleSelect(s)}
              style={{
                width: '100%', padding: '10px 14px', textAlign: 'left',
                background: 'transparent', border: 'none', cursor: 'pointer',
                borderBottom: i < suggestions.length - 1 ? '1px solid var(--border)' : 'none',
                transition: 'background 0.1s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <span style={{ fontSize: 16, marginTop: 1, flexShrink: 0 }}>📍</span>
                <div>
                  {/* Adresse courte en gras */}
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>
                    {s.short || s.label.split(',')[0]}
                  </div>
                  {/* Adresse complète en gris */}
                  <div style={{ fontSize: 12, color: 'var(--text-3)', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '400px' }}>
                    {s.label}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {error && <span style={{ fontSize: '12px', color: '#EF4444' }}>{error}</span>}
    </div>
  );
}
